/**
 * Helper functions for managing song play history in services
 */

import songService from '../services/songService';
import { getDetailedRelativeTime } from './songDates';

/**
 * Get service play details as separate parts for custom rendering
 * @param {Object} play - A service play ({ serviceTitle, date })
 * @returns {Object|null} - Object with serviceTitle and timeAgo, or null
 */
export const getServicePlayDetails = (play) => {
  if (!play) {
    return null;
  }

  const { serviceTitle, date } = play;
  const timeAgo = getDetailedRelativeTime(date);

  return { serviceTitle, timeAgo };
};

/**
 * Get the time category for badge coloring
 * @param {Object} play - A service play ({ serviceTitle, date })
 * @returns {string|null} - 'red', 'yellow', 'green', or null if no date
 *
 * Color legend:
 * - red: less than 1 month ago
 * - yellow: less than 2 months ago but more than 1 month
 * - green: 2 months or more ago
 */
export const getServicePlayTimeCategory = (play) => {
  if (!play?.date) {
    return null;
  }

  const date = new Date(play.date);
  const now = new Date();
  const diffMs = now - date;

  const ONE_MONTH_IN_MS = 30 * 24 * 60 * 60 * 1000;
  const TWO_MONTHS_IN_MS = 60 * 24 * 60 * 60 * 1000;

  if (diffMs < ONE_MONTH_IN_MS) {
    return 'red';
  } else if (diffMs < TWO_MONTHS_IN_MS) {
    return 'yellow';
  } else {
    return 'green';
  }
};

/**
 * Update songs with service play info and persist to backend
 * @param {Array} songsToUpdate - Songs to update with service play info
 * @param {string} serviceTitle - Name of the service
 * @param {Array} allSongs - All songs array to update with saved data
 * @returns {Promise<Array>} - Updated songs array with persisted data
 */
export const persistSongsServicePlay = async (songsToUpdate, serviceTitle, allSongs) => {
  const songUpdatePromises = songsToUpdate.map(async (song) => {
    try {
      const lastServicePlay = {
        serviceTitle,
        date: new Date().toISOString(),
      };

      const response = await songService.updateSongProperties(song._id, { lastServicePlay });

      return response.data || response;
    } catch (error) {
      console.error(`Failed to update song ${song._id}:`, error);

      return allSongs.find((s) => s._id === song._id) || song;
    }
  });

  const savedSongs = await Promise.all(songUpdatePromises);
  let updatedSongsArray = [...allSongs];

  savedSongs.forEach((savedSong) => {
    const songIndex = updatedSongsArray.findIndex((s) => s._id === savedSong._id);
    if (songIndex !== -1) {
      updatedSongsArray[songIndex] = savedSong;
    }
  });

  return updatedSongsArray;
};
